/**
 * Comms session browser + human-response inbox client.
 * Extends the discussion SoR without forking Approvals; wraps /v1/comms/*.
 */
import { getDevtoolsApiUrl } from '../utils/env';
import { getTokenFromStorage } from '../utils/tokenManager';
import { withRetry } from '../utils/withRetry';
import type {
  AgentPresence,
  CommsConversation,
  CommsMessage,
  CommsParticipant,
  CommsWorkItem,
} from './devtoolsApi';
import { presenceApi } from './devtoolsApi';

export type { AgentPresence, CommsConversation, CommsMessage, CommsParticipant, CommsWorkItem };

/** Session row extras returned by conversations/search after the Comms UX refactor. */
export type CommsSession = CommsConversation & {
  sessionId?: string;
  ownerPresenceId?: string | null;
  primaryRepo?: string;
  prNumber?: string;
  needsInspection?: boolean;
  dateCreated?: string | null;
};

export type NeedsResponseMeta = {
  status?: string;
  kind?: string;
  assigneeUserIds?: string[];
  actions?: string[];
  workItemId?: string;
  decision?: string | null;
  resolvedByUserId?: string | null;
  resolvedAt?: string | null;
  source?: { system?: string; id?: string | null };
};

export type CommsChatMessage = CommsMessage & {
  meta?: {
    needsResponse?: NeedsResponseMeta;
    workItemId?: string;
    speechAct?: string;
    inReplyTo?: string;
    decision?: string;
  };
};

export type InboxItem = {
  conversationId: string;
  messageId: string;
  title?: string;
  preview: string;
  needsResponse?: NeedsResponseMeta;
  workItemId?: string | null;
  authorKind?: string;
  authorId?: string;
  createdAt?: string;
  deepLink?: string;
};

export type SessionSearchParams = {
  q?: string;
  repo?: string;
  pr?: string;
  humanUserId?: string;
  agentPresenceId?: string;
  agentName?: string;
  attention?: string;
  sort?: 'created_asc' | 'created_desc';
};

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getTokenFromStorage();
  const exec = async () => {
    const res = await fetch(`${getDevtoolsApiUrl()}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
        detail?: string;
      };
      const err = new Error(
        body.error || body.message || body.detail || `HTTP ${res.status}`,
      ) as Error & { status: number };
      err.status = res.status;
      throw err;
    }
    if (res.status === 204) {
      return undefined as T;
    }
    return res.json() as Promise<T>;
  };
  return withRetry(exec);
}

function withQuery(path: string, params: Record<string, string | undefined>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') qs.set(key, value);
  }
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

/**
 * Session-oriented Comms API (Slack-like browser + needs_response inbox).
 * Side effects: GET/POST against /v1/comms…; resolve flips ask meta (not mark-read).
 */
export const sessionCommsApi = {
  listSessions: (params: SessionSearchParams = {}) =>
    request<{ conversations: CommsSession[] }>(
      withQuery('/v1/comms/conversations/search', {
        q: params.q,
        repo: params.repo,
        pr: params.pr,
        humanUserId: params.humanUserId,
        agentPresenceId: params.agentPresenceId,
        agentName: params.agentName,
        attention: params.attention,
        sort: params.sort,
      }),
    ),

  inbox: () =>
    request<{ items: InboxItem[]; openCount: number }>('/v1/comms/inbox'),

  unread: () =>
    request<{
      unreadConversations: number;
      unreadMessages: number;
      conversations: { conversationId: string; unreadCount: number }[];
    }>('/v1/comms/unread'),

  get: (id: string) =>
    request<{ conversation: CommsSession }>(
      `/v1/comms/conversation/${encodeURIComponent(id)}`,
    ),

  create: (body: {
    title: string;
    kind?: string;
    ownerPresenceId?: string;
    primaryRepo?: string;
    prNumber?: string;
    linkedPlanId?: string;
    humanUserIds?: string[];
  }) =>
    request<{ conversation: CommsSession }>('/v1/comms/conversation/create', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  messages: (id: string) =>
    request<{ messages: CommsChatMessage[] }>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/messages/search`,
    ),

  postMessage: (
    id: string,
    body: {
      body: string;
      authorKind?: string;
      role?: string;
      wakeMonitors?: boolean;
      meta?: Record<string, unknown>;
      assigneeUserIds?: string[];
    },
  ) =>
    request<{ message: CommsChatMessage; wakeResults: unknown[] }>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/message/create`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  resolve: (
    messageId: string,
    body: {
      decision: 'approve' | 'reject' | 'reply';
      body?: string;
      resumeWork?: boolean;
      presenceId?: string;
      team_id?: string;
    },
  ) =>
    request<{ ok: boolean; decision: string; response?: CommsChatMessage }>(
      `/v1/comms/message/${encodeURIComponent(messageId)}/resolve`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  markRead: (id: string, messageId?: string) =>
    request<Record<string, unknown>>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/read`,
      { method: 'POST', body: JSON.stringify({ messageId }) },
    ),

  mention: (
    id: string,
    body: { presenceIds?: string[]; humanUserIds?: string[]; note?: string },
  ) =>
    request<Record<string, unknown>>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/mention`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  workItems: (id: string) =>
    request<{ workItems: CommsWorkItem[] }>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/work-items/search`,
    ),

  proposeWork: (
    id: string,
    body: {
      title: string;
      brief: { goal: string };
      doneWhen: string;
      writeScope?: string[];
    },
  ) =>
    request<{ workItem: CommsWorkItem }>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/work-item/create`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  handoff: (
    id: string,
    body: {
      title: string;
      brief: { goal: string };
      doneWhen: string;
      writeScope?: string[];
      toPresenceId: string;
    },
  ) =>
    request<{ workItem: CommsWorkItem }>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/handoff`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  claimWork: (workItemId: string, presenceId: string) =>
    request<{ workItem: CommsWorkItem }>(
      `/v1/comms/work-item/${encodeURIComponent(workItemId)}/claim`,
      { method: 'POST', body: JSON.stringify({ presenceId }) },
    ),

  workStatus: (
    workItemId: string,
    body: {
      presenceId?: string;
      state: string;
      reason?: string;
      verification?: string;
      artifactRef?: string;
    },
  ) =>
    request<{ workItem: CommsWorkItem }>(
      `/v1/comms/work-item/${encodeURIComponent(workItemId)}/status`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  listPresence: (onlineOnly = true) => presenceApi.list(onlineOnly),
};
